import { useEffect, useState, useCallback } from "react";
import { Archive, Download, MapPin, Cpu, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import { useInvalidate } from "@/hooks/useDeviceLive";

export default function AdminHistory() {
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");
  const [entries, setEntries] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/tenants").then((r) => setTenants(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const url = tenantFilter ? `/admin/history?tenant_id=${tenantFilter}` : "/admin/history";
    api.get(url).then((r) => setEntries(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tenantFilter]);
  useEffect(() => { load(); }, [load]);
  useInvalidate(["history", "tenants"], load, { admin: true });

  const current = entries.find((e) => e.id === selectedId) || entries[0] || null;

  const download = async (entry) => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/history/${entry.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `KyraLink_${entry.serial}_history_report.pdf` });
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const s = current?.sizing;
  const u = current?.usage;

  return (
    <div className="h-full grid grid-cols-[380px_1fr]" data-testid="admin-history-page">
      <aside className="border-r border-kl-lineLight flex flex-col bg-kl-light">
        <div className="p-6 border-b border-kl-lineLight">
          <p className="kl-label">Platform history</p>
          <h1 className="font-display font-light text-2xl mt-1">All history <span className="font-mono text-sm text-kl-mutedLight">{entries.length}</span></h1>
          <select value={tenantFilter} onChange={(e) => { setTenantFilter(e.target.value); setSelectedId(null); }} className="kl-input-box h-9 mt-3 w-full" data-testid="admin-history-tenant-filter">
            <option value="">All dealers</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <ul className="flex-1 overflow-y-auto kl-scroll-light">
          {entries.length === 0 && <li className="p-6 text-sm text-kl-mutedLight" data-testid="admin-history-empty">Nothing in history yet.</li>}
          {entries.map((e) => (
            <li key={e.id} className={`border-b border-kl-lineLight ${current?.id === e.id ? "bg-white" : "hover:bg-white"} transition-colors`} data-testid={`admin-history-row-${e.id}`}>
              <button onClick={() => setSelectedId(e.id)} className="w-full text-left p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display text-sm truncate">{e.client_name}</span>
                  <span className="kl-label">{e.client_type}</span>
                </div>
                <div className="text-[11px] text-kl-mutedLight mt-1 flex items-center gap-1.5"><Building2 size={11} /> {e.tenant_name || "—"}</div>
                <div className="font-mono text-[11px] text-kl-mutedLight mt-1 truncate">{e.serial} · {fmt.date(e.confirmed_at)}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="overflow-y-auto kl-scroll-light">
        {current ? (
          <div className="max-w-4xl mx-auto p-8" data-testid="admin-history-detail">
            <div className="flex items-start justify-between gap-6 pb-6 border-b border-kl-lineLight">
              <div className="min-w-0">
                <p className="kl-label">{current.tenant_name} · {current.serial} · confirmed {fmt.time(current.confirmed_at)} by {current.confirmed_by}</p>
                <h2 className="font-display font-light text-3xl mt-1 truncate">{current.client_name}</h2>
                {(current.address || current.city || current.country) && (
                  <p className="text-sm text-kl-mutedLight mt-1 flex items-start gap-1.5"><MapPin size={13} className="shrink-0 mt-0.5" /> {[current.address, current.city, current.country].filter(Boolean).join(", ")}</p>
                )}
              </div>
              <button onClick={() => download(current)} disabled={busy} className="kl-btn-dark shrink-0" data-testid="admin-history-download"><Download size={15} /> Download PDF</button>
            </div>

            <div className="grid grid-cols-12 gap-6 mt-6">
              <div className="col-span-12 lg:col-span-7 space-y-4">
                <div className="kl-panel p-5">
                  <p className="kl-label">Measured consumption</p>
                  <div className="grid grid-cols-3 gap-px bg-kl-lineLight border border-kl-lineLight mt-3">
                    {[["Avg daily", `${u?.avg_daily_kwh ?? 0} kWh`], ["Peak load", `${u?.peak_kw ?? 0} kW`], ["Total", `${u?.total_kwh ?? 0} kWh`]].map(([l, v]) => (
                      <div key={l} className="bg-white p-3"><div className="kl-label">{l}</div><div className="font-mono text-lg mt-1">{v}</div></div>
                    ))}
                  </div>
                  <p className="text-xs text-kl-mutedLight mt-2">{u?.days ?? 0} days monitored · {u?.readings_count ?? 0} samples</p>
                </div>
                <div className="kl-panel p-5">
                  <p className="kl-label">{current.report_title}</p>
                  <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap">{current.report_body}</p>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-5">
                <div className="kl-panel-dark p-5">
                  <p className="kl-label-dark flex items-center gap-1.5"><Cpu size={12} /> Recommended system</p>
                  {s && (
                    <div className="mt-3 space-y-2 font-mono text-sm">
                      <div className="flex justify-between"><span className="text-kl-muted">Panels</span><span>{s.pv?.count} × {s.pv?.panel?.rating} W</span></div>
                      <div className="flex justify-between"><span className="text-kl-muted">Array</span><span>{s.pv?.array_kw} kWp</span></div>
                      <div className="flex justify-between"><span className="text-kl-muted">Inverter</span><span>{s.inverter?.count} × {s.inverter?.recommended?.rating} kW</span></div>
                      <div className="flex justify-between"><span className="text-kl-muted">Battery</span><span>{s.battery?.total_kwh} kWh</span></div>
                      <div className="flex justify-between border-t border-kl-line pt-2 mt-2"><span className="text-kl-muted">Cost</span><span className="text-kl-green">{fmt.money(s.economics?.system_cost, s.economics?.currency)}</span></div>
                      <div className="flex justify-between"><span className="text-kl-muted">Payback</span><span>{s.economics?.payback_years} yrs</span></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center p-10" data-testid="admin-history-empty-state">
            <Archive size={28} className="text-kl-mutedLight" />
            <h2 className="font-display text-2xl mt-4">No history yet</h2>
            <p className="text-sm text-kl-mutedLight mt-1 max-w-sm">When a dealer confirms a proposal, its full snapshot shows up here.</p>
          </div>
        )}
      </section>
    </div>
  );
}
